import React, { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'liveBadgePosition';
const MARGIN = 10;
const DRAG_THRESHOLD = 5;

const readSaved = () => {
  try {
    const p = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) return p;
  } catch {
    /* ignore */
  }
  return null;
};

/**
 * Wraps children in a fixed, freely draggable container (mouse + touch via Pointer Events).
 * Position persists in localStorage and is clamped to the viewport.
 * Default position: top-right at `defaultTop`.
 */
export default function DraggableBadge({ children, defaultTop = 10 }) {
  const ref = useRef(null);
  const drag = useRef({ active: false, moved: false, sx: 0, sy: 0, ox: 0, oy: 0 });
  const [pos, setPos] = useState(readSaved); // null => default anchored top-right
  const [dragging, setDragging] = useState(false);

  const clamp = useCallback((x, y) => {
    const el = ref.current;
    const w = el ? el.offsetWidth : 150;
    const h = el ? el.offsetHeight : 40;
    return {
      x: Math.min(Math.max(MARGIN, x), Math.max(MARGIN, window.innerWidth - w - MARGIN)),
      y: Math.min(Math.max(MARGIN, y), Math.max(MARGIN, window.innerHeight - h - MARGIN)),
    };
  }, []);

  useEffect(() => {
    const onResize = () => setPos((p) => (p ? clamp(p.x, p.y) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clamp]);

  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const rect = ref.current.getBoundingClientRect();
    drag.current = { active: true, moved: false, sx: e.clientX, sy: e.clientY, ox: rect.left, oy: rect.top };
    ref.current.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.active) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    if (!d.moved) setDragging(true);
    d.moved = true;
    setPos(clamp(d.ox + dx, d.oy + dy));
  };

  const endDrag = (e) => {
    const d = drag.current;
    if (!d.active) return;
    d.active = false;
    ref.current?.releasePointerCapture?.(e.pointerId);
    setDragging(false);
    if (d.moved) {
      setPos((p) => {
        if (p) localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
        return p;
      });
    }
  };

  // Swallow the click that follows a drag so the badge action doesn't fire.
  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.stopPropagation();
      e.preventDefault();
      drag.current.moved = false;
    }
  };

  const style = {
    position: 'fixed',
    zIndex: 10000,
    touchAction: 'none',
    userSelect: 'none',
    cursor: dragging ? 'grabbing' : 'grab',
    top: pos ? `${pos.y}px` : `${defaultTop}px`,
    ...(pos ? { left: `${pos.x}px` } : { right: `${MARGIN}px` }),
  };

  return (
    <div
      ref={ref}
      style={style}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClickCapture={onClickCapture}
    >
      {children}
    </div>
  );
}
